import React, { useEffect, useMemo, useState } from "react";
import { getAllStudents, refreshStudentsFromFirestore } from "../../services/studentsService";
import { getTransactions } from "../../services/apiClient";
import dataService from "../../services/dataService";
import { getCurrentUser } from "../../auth";
import "./Dashboard.css";
import { Chart } from "react-chartjs-2";
import "chart.js/auto";
import jsPDF from "jspdf";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { db } from "../../services/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { normalizeCampusKey, parseMoneyAmount } from "../../utils/feeCalculator";

export default function Dashboard() {
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState(() => {
    try {
      return dataService.getAllExpenses();
    } catch {
      return [];
    }
  });
  const currentUser = getCurrentUser();
  const userCampus = currentUser?.campus;
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
  const [campusFilter, setCampusFilter] = useState(() => (isAdmin ? "" : (userCampus || "")));

  // Students cache (local + Firestore refresh)
  const [studentsList, setStudentsList] = useState(() => getAllStudents());
  useEffect(() => {
    (async () => {
      try {
        const list = await refreshStudentsFromFirestore();
        if (Array.isArray(list) && list.length) setStudentsList(list);
      } catch {
        setStudentsList(getAllStudents());
      }
    })();
  }, []);

  useEffect(() => {
    // Initialize from local storage or API via TransactionsService
    (async () => {
      try {
        const { getPayments } = await import("../../services/transactionsService");
        const txs = await getPayments();
        if (Array.isArray(txs)) setPayments(txs);
      } catch (err) {
        setPayments([]);
      }
    })();
  }, []);

  useEffect(() => {
    let local = [];
    try {
      local = Array.isArray(dataService.getAllExpenses()) ? dataService.getAllExpenses() : [];
    } catch {
      local = [];
    }
    const normalizeDate = (d) => {
      if (!d) return "";
      if (typeof d === "string") return d.slice(0, 10);
      if (d instanceof Date) return d.toISOString().slice(0, 10);
      if (typeof d?.toDate === "function") return d.toDate().toISOString().slice(0, 10);
      return String(d).slice(0, 10);
    };
    const normalizeExpense = (e) => ({
      ...e,
      campus: normalizeCampusKey(e?.campus),
      date: normalizeDate(e?.date),
      amount: parseMoneyAmount(e?.amount)
    });
    local = local.map(normalizeExpense);
    setExpenses(local);

    let unsub = null;
    try {
      unsub = onSnapshot(
        collection(db, "expenses"),
        (snap) => {
          const cloud = snap.docs.map(d => normalizeExpense({ id: d.id, ...d.data() }));
          const key = (x) => `${x.date}|${x.campus}|${x.amount}|${String(x.description || "")}|${String(x.accountId || "")}`;
          const merged = new Map();
          local.forEach(x => merged.set(key(x), x));
          cloud.forEach(x => merged.set(key(x), x));
          setExpenses(Array.from(merged.values()));
        },
        () => setExpenses(local)
      );
    } catch {
      unsub = null;
    }
    return () => {
      try { if (typeof unsub === "function") unsub(); } catch { void 0; }
    };
  }, []);

  const gatedPayments = useMemo(() => {
    if (isAdmin) return payments;
    const uc = normalizeCampusKey(userCampus);
    const sMap = new Map((studentsList || []).map(s => [String(s.id), String(s.campus || "").toLowerCase()]));
    return payments.filter(p => {
      const pc = normalizeCampusKey(p.campus);
      if (pc) return pc === uc;
      const sc = sMap.get(String(p.studentId || ""));
      return normalizeCampusKey(sc) === uc;
    });
  }, [payments, isAdmin, userCampus, studentsList]);
  const gatedExpenses = useMemo(() => {
    if (isAdmin) return expenses;
    const uc = normalizeCampusKey(userCampus);
    return expenses.filter(e => {
      const ec = normalizeCampusKey(e?.campus);
      return ec ? ec === uc : true;
    });
  }, [expenses, isAdmin, userCampus]);

  const campusSet = useMemo(() => {
    const set = new Set();
    // Always include known campuses for admin so the selector can switch even if no data yet
    if (isAdmin) {
      set.add("gapan");
      set.add("bulacan");
    }
    gatedPayments.forEach(p => {
      const c = (p.campus || "").toLowerCase().trim();
      if (c) set.add(c.includes("gapan") ? "gapan" : (c.includes("bulacan") ? "bulacan" : c));
    });
    gatedExpenses.forEach(e => {
      const c = (e.campus || "").toLowerCase().trim();
      if (c) set.add(c.includes("gapan") ? "gapan" : (c.includes("bulacan") ? "bulacan" : c));
    });
    return Array.from(set);
  }, [gatedPayments, gatedExpenses, isAdmin]);

  const filteredPayments = useMemo(() => {
    if (!campusFilter) return gatedPayments;
    const cf = normalizeCampusKey(campusFilter);
    return gatedPayments.filter(p => normalizeCampusKey(p.campus) === cf);
  }, [gatedPayments, campusFilter]);
  const filteredExpenses = useMemo(() => {
    if (!campusFilter) return gatedExpenses;
    const cf = normalizeCampusKey(campusFilter);
    return gatedExpenses.filter(e => {
      const ec = normalizeCampusKey(e?.campus);
      return ec ? ec === cf : true;
    });
  }, [gatedExpenses, campusFilter]);

  const totalCollections = useMemo(() => filteredPayments.reduce((s, p) => s + parseMoneyAmount(p.amount), 0), [filteredPayments]);
  const totalExpenses = useMemo(() => filteredExpenses.reduce((s, p) => s + parseMoneyAmount(p.amount), 0), [filteredExpenses]);
  const total = totalCollections;
  const recent = useMemo(() => filteredPayments.slice(-5).reverse(), [filteredPayments]);
  const count = filteredPayments.length;
  const avg = useMemo(() => (count ? Math.round((total / count) * 100) / 100 : 0), [total, count]);
  const today = useMemo(() => {
    const d0 = new Date();
    const y = d0.getFullYear();
    const m = d0.getMonth();
    const day = d0.getDate();
    return filteredPayments.filter(p => {
      const d = new Date(p.date);
      return d.getFullYear() === y && d.getMonth() === m && d.getDate() === day;
    }).reduce((s, p) => s + Number(p.amount || 0), 0);
  }, [filteredPayments]);
  const monthTotal = useMemo(() => {
    const d0 = new Date();
    const y = d0.getFullYear();
    const m = d0.getMonth();
    return filteredPayments.filter(p => {
      const d = new Date(p.date);
      return d.getFullYear() === y && d.getMonth() === m;
    }).reduce((s, p) => s + Number(p.amount || 0), 0);
  }, [filteredPayments]);

  const byCampus = useMemo(() => {
    const map = new Map();
    gatedPayments.forEach(p => {
      const key = (p.campus || "").toLowerCase() || "unknown";
      map.set(key, (map.get(key) || 0) + Number(p.amount || 0));
    });
    gatedExpenses.forEach(e => {
      const key = (e.campus || "").toLowerCase() || "unknown";
      map.set(key, map.get(key) || 0);
    });
    const labels = Array.from(map.keys());
    const collVals = labels.map(l => gatedPayments.filter(p => (p.campus || "").toLowerCase() === l).reduce((s, p) => s + Number(p.amount || 0), 0));
    const expVals = labels.map(l => gatedExpenses.filter(e => (e.campus || "").toLowerCase() === l).reduce((s, e) => s + Number(e.amount || 0), 0));
    return { labels, collVals, expVals };
  }, [gatedPayments, gatedExpenses]);

  const byMethod = useMemo(() => {
    const map = new Map();
    filteredPayments.forEach(p => {
      const key = p.method || "Unknown";
      map.set(key, (map.get(key) || 0) + 1);
    });
    const labels = Array.from(map.keys());
    const values = labels.map(l => map.get(l));
    return { labels, values };
  }, [filteredPayments]);

  const formatName = (s) => {
    if (!s) return "";
    const last = String(s.lastName || s.lastname || "").trim();
    const first = String(s.firstName || s.firstname || "").trim();
    const middle = String(s.middleName || s.middlename || s.middleInitial || "").trim();
    const core = `${last}${last && first ? ", " : ""}${first}`;
    const fallback = s.fullname || s.name || "";
    return fallback || (middle ? `${core} ${middle}` : core);
  };

  const lastDays = useMemo(() => {
    const days = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const coll = filteredPayments.filter(p => String(p.date || "").slice(0, 10) === key).reduce((s, p) => s + parseMoneyAmount(p.amount), 0);
      const exp = filteredExpenses.filter(e => String(e.date || "").slice(0, 10) === key).reduce((s, e) => s + parseMoneyAmount(e.amount), 0);
      days.push({ label: key.slice(5), coll, exp });
    }
    return days;
  }, [filteredPayments, filteredExpenses]);

  const topStudents = useMemo(() => {
    const sums = new Map();
    // Use scoped, filtered payments so non-admin users only see their campus and
    // the Campus selector also applies to the ranking.
    filteredPayments.forEach(p => {
      const id = p.studentId || "";
      if (!id) return;
      sums.set(id, (sums.get(id) || 0) + Number(p.amount || 0));
    });
    const arr = Array.from(sums.entries()).map(([id, amt]) => {
      const s = studentsList.find(x => String(x.id) === String(id));
      const name = s ? (s.fullname || s.name || `${String(s.lastName || s.lastname || "")}, ${String(s.firstName || s.firstname || "")}`.trim()) : id;
      const campus = (s?.campus || "").toLowerCase() || (filteredPayments.find(p => p.studentId === id)?.campus || "");
      return { id, name, campus, amount: amt };
    }).sort((a, b) => b.amount - a.amount).slice(0, 5);
    return arr;
  }, [filteredPayments, studentsList]);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Finance Dashboard Summary", 14, 18);
    doc.setFontSize(12);
    doc.text(`Total Collection: ₱${total.toLocaleString()}`, 14, 30);
    doc.text(`Transactions: ${count}`, 14, 38);
    doc.text(`Average per Transaction: ₱${avg.toLocaleString()}`, 14, 46);
    doc.text(`Today: ₱${today.toLocaleString()}  |  This Month: ₱${monthTotal.toLocaleString()}`, 14, 54);
    doc.text("Recent Transactions:", 14, 66);
    const rows = recent.slice(0, 5).map(r => [r.date, r.studentId, r.campus || "", r.method || "", String(r.amount)]);
    let y = 74;
    rows.forEach(r => {
      doc.text(r.join("  |  "), 14, y);
      y += 8;
    });
    doc.save("finance_dashboard_summary.pdf");
  };

  return (
    <div className="container mt-4">
      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <label className="form-label">Campus</label>
          <select
            className="form-select"
            value={campusFilter}
            onChange={e => setCampusFilter(e.target.value)}
            disabled={!isAdmin}
          >
            {isAdmin && <option value="">All Campuses</option>}
            {campusSet.map(c => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
        <div className="col-md-3">
          <div className="card summary-card h-100 secondary">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Data Source</span>
              <i className="fas fa-database text-primary"></i>
            </div>
            <div className="fw-bold">
              {(() => {
                let source = USE_LOCAL_FALLBACK ? "Local (Dev)" : "Firestore";
                try {
                  const force = localStorage.getItem("forceFirestore");
                  if (String(force).toLowerCase() === "true") source = "Firestore (forced)";
                } catch (e) {
                  // ignore
                }
                return source;
              })()}
            </div>
            {USE_LOCAL_FALLBACK && (
              <button
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={() => {
                  try {
                    localStorage.setItem("forceFirestore", "true");
                  } catch (e) {
                    // ignore
                  }
                  window.location.reload();
                }}
              >
                Use Firestore on this device
              </button>
            )}
          </div>
        </div>
        <div className="col-md-3">
          <div className="card summary-card compact h-100 info">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Avg/Txn • Today • Month</span>
              <i className="fas fa-chart-line text-success"></i>
            </div>
            <div className="summary-value">₱{avg.toLocaleString()} • ₱{today.toLocaleString()} • ₱{monthTotal.toLocaleString()}</div>
          </div>
        </div>
      </div>
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card summary-card h-100 success">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Total Collection</span>
              <i className="fas fa-coins text-warning"></i>
            </div>
            <div className="summary-value">₱{total.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card summary-card h-100 danger">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Expenses</span>
              <i className="fas fa-file-invoice-dollar text-danger"></i>
            </div>
            <div className="summary-value">₱{totalExpenses.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card summary-card h-100 primary">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Net Cash Flow</span>
              <i className="fas fa-chart-line text-success"></i>
            </div>
            <div className="summary-value">₱{(total - totalExpenses).toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div className="card p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="mb-0">Recent Transactions</h5>
          <button className="btn btn-outline-primary" onClick={exportPDF}>Export Summary (PDF)</button>
        </div>
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Student ID</th>
              <th>Student Name</th>
              <th>Campus</th>
              <th>Method</th>
              <th className="text-end">Amount</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((tx, idx) => {
              const student = studentsList.find(s => String(s.id) === String(tx.studentId));
              return (
                <tr key={idx}>
                  <td>{tx.date}</td>
                  <td>{tx.studentId}</td>
                  <td>
                    {student ? (
                      <a href={`/sis/students/${student.id}/profile`} className="text-decoration-none">
                        {formatName(student)}
                      </a>
                    ) : (tx.payer || "")}
                  </td>
                  <td>{tx.campus || student?.campus || ""}</td>
                  <td>{tx.method}</td>
                  <td className="text-end">₱{Number(tx.amount || 0).toLocaleString()}</td>
                </tr>
              );
            })}
            {recent.length === 0 && (
              <tr><td colSpan="6" className="text-center text-muted">No transactions yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="row g-3 mt-3">
        <div className="col-md-6">
          <div className="card p-3">
            <h6 className="card-header">Collections vs Expenses • Last 14 Days</h6>
            <Chart
              type="line"
              data={{
                labels: lastDays.map(d => d.label),
                datasets: [
                  {
                    label: "Collections",
                    data: lastDays.map(d => d.coll),
                    borderColor: "#28a745",
                    backgroundColor: "rgba(40,167,69,0.15)",
                    tension: 0.3,
                    fill: true
                  },
                  {
                    label: "Expenses",
                    data: lastDays.map(d => d.exp),
                    borderColor: "#dc3545",
                    backgroundColor: "rgba(220,53,69,0.15)",
                    tension: 0.3,
                    fill: true
                  }
                ]
              }}
              options={{ responsive: true, plugins: { legend: { display: true } }, scales: { y: { beginAtZero: true } } }}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="card p-3">
            <h6 className="card-header">Collections vs Expenses by Campus</h6>
            <Chart
              type="bar"
              data={{
                labels: byCampus.labels,
                datasets: [
                  {
                    label: "Collections",
                    data: byCampus.collVals,
                    backgroundColor: "rgba(255, 193, 7, 0.7)",
                    borderColor: "#ffc107",
                    borderWidth: 1
                  },
                  {
                    label: "Expenses",
                    data: byCampus.expVals,
                    backgroundColor: "rgba(220, 53, 69, 0.7)",
                    borderColor: "#dc3545",
                    borderWidth: 1
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: { legend: { display: true } },
                scales: { y: { beginAtZero: true } }
              }}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="card p-3">
            <h6 className="card-header">Transactions by Method</h6>
            <Chart
              type="pie"
              data={{
                labels: byMethod.labels,
                datasets: [{
                  label: "Count",
                  data: byMethod.values,
                  backgroundColor: ["#ffc107", "#28a745", "#17a2b8", "#6f42c1", "#dc3545"],
                  borderColor: "#fff",
                  borderWidth: 1
                }]
              }}
              options={{ responsive: true }}
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="card p-3">
            <h6 className="card-header">Top Payers</h6>
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Student</th>
                  <th>Campus</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {topStudents.map((s, i) => (
                  <tr key={i}>
                    <td>{s.name}</td>
                    <td>{s.campus}</td>
                    <td className="text-end">₱{Number(s.amount).toLocaleString()}</td>
                  </tr>
                ))}
                {topStudents.length === 0 && (
                  <tr><td colSpan="3" className="text-center text-muted">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
